import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import { ScrollArea } from "@/components/ui/scroll-area";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";

import { Calendar as CalendarIcon } from "lucide-react";

import Moment from 'moment';
import * as XLSX from 'xlsx';
import { toast } from 'react-toastify';

import ServiceInventory from 'services/serviceInventory';

import CardAccount from 'core/widgets/ui/card/CardAccount';

export default function DigMaterialAccount(props) {

    const [records, setRecords] = useState([]);

    const [filter, setFilter] = useState({ "accNumber": props.accNumber });
    const [account, setAccount] = useState({});

    const [date, setDate] = useState({
        from: Moment().startOf('month').toDate(),
        to: Moment().toDate(),
    });

    const [query, setQuery] = useState('');

    const [pageSize, setPageSize] = useState(10);
    const [pageCurrent, setPageCurrent] = useState(0);
    const [pageTotal, setPageTotal] = useState(0);

    const [isDate, setIsDate] = useState(false);

    const navigate = useNavigate();
    const invService = new ServiceInventory();

    const handleOnChange = (e) => {
        var { value, name } = e.target;
        switch (name) {
            case 'pageSize':
                setPageSize(value);
                break;
            case 'search':
                setQuery(value);
                break;
        }
    }

    const onDateChange = (event) => {
        setIsDate(true);
        setDate(event);
    }

    const getChipColor = (sts) => {
        switch (sts) {
            case "active":
                return "bg-green-100 text-green-800";
            case "error":
                return "bg-red-100 text-red-800";
            default:
                return "bg-gray-200 text-gray-900";
        }
    }

    const isAccount = () => {
        if (account === null || account === undefined) {
            return true;
        } else {
            if (Object.keys(account).length === 0) {
                return true;
            } else {
                return false;
            }
        }
    }

    const exportData = (type) => {
        (async function () {
            let body = {
                "filter": filter,
                "page": 0,
                "size": 0,
                "searchQuery": query,
                "startDate": isDate ? date.from.toISOString() : "",
                "endDate": isDate ? Moment(date.to).add(1, 'day').toDate().toISOString() : ""
            };
            var res = await invService.getAllTansByAcc(body);
            var records = res.data.data;
            let items = [];
            records.forEach((item) => {
                items.push({
                    accNumber: item.accNumber,
                    transactionType: item.transactionType,
                    amount: item.amount,
                    balanceAfter: item.balanceAfter,
                    narration: item.narration,
                    status: item.status,
                    createdAt: item.createdAt,
                });
            });

            if (type === "xlsx") {
                const worksheet = XLSX.utils.json_to_sheet(items);
                const workbook = XLSX.utils.book_new();
                XLSX.utils.book_append_sheet(workbook, worksheet, "statement");
                XLSX.writeFile(workbook, "Statement.xlsx");
            } else {
                let headers = ['accNumber, transactionType, amount, balanceAfter, narration, status, createdAt'];
                let recordCsv = items.reduce((acc, record) => {
                    const { accNumber, transactionType, amount, balanceAfter, narration, status, createdAt } = record
                    acc.push([accNumber, transactionType, amount, balanceAfter, narration, status, createdAt].join(','))
                    return acc
                }, []);
                downloadFile({
                    data: [...headers, ...recordCsv].join('\n'),
                    fileName: 'Statement.csv',
                    fileType: 'text/csv',
                });
            }
        })();
    }

    const downloadFile = ({ data, fileName, fileType }) => {
        const blob = new Blob([data], { type: fileType })
        const a = document.createElement('a')
        a.download = fileName
        a.href = window.URL.createObjectURL(blob)
        const clickEvt = new MouseEvent('click', {
            view: window,
            bubbles: true,
            cancelable: true,
        })
        a.dispatchEvent(clickEvt)
        a.remove()
    }

    const pageChange = (pageValue) => {
        (async function () {
            let body = {
                "filter": filter,
                "page": pageValue,
                "size": parseInt(pageSize),
                "searchQuery": query,
                "startDate": isDate ? date.from.toISOString() : "",
                "endDate": isDate ? Moment(date.to).add(1, 'day').toDate().toISOString() : ""
            };
            var res = await invService.getAllTansByAcc(body);
            if (res['status']) {
                setPageCurrent(res.data.currentPage);
                setPageTotal(res.data.totalPage);
                setRecords(res.data.data);
                setAccount(props.productid);
            } else {
                toast.error('Server error please try again', { position: "top-right", autoClose: 2000, hideProgressBar: false, closeOnClick: true, progress: undefined, theme: "light" });
            }
        })();
    }

    const loadData = () => {
        (async function () {
            let body = {
                "filter": filter,
                "page": 1,
                "size": parseInt(pageSize),
                "searchQuery": query,
                "startDate": isDate ? date.from.toISOString() : "",
                "endDate": isDate ? Moment(date.to).add(1, 'day').toDate().toISOString() : ""
            };
            var res = await invService.getAllTansByAcc(body);
            if (res['status']) {
                setPageCurrent(res.data.currentPage);
                setPageTotal(res.data.totalPage);
                setRecords(res.data.data);
                setAccount(props.productid);
            } else {
                toast.error('Server error please try again', { position: "top-right", autoClose: 2000, hideProgressBar: false, closeOnClick: true, progress: undefined, theme: "light" });
            }
        })();
    }

    const nextPage = () => {
        if (pageCurrent < pageTotal) {
            pageChange(pageCurrent + 1);
        }
    }

    const prevPage = () => {
        if (pageCurrent > 1) {
            pageChange(pageCurrent - 1);
        }
    }

    useEffect(() => {
        loadData();
    }, []);

    useEffect(() => {
        loadData();
    }, [pageSize, query, date, filter]);

    return (
        <div className='overflow-y-auto overflow-x-hidden z-[100] w-full h-modal md:h-full fixed top-0 left-0 bg-black bg-opacity-70'>
            <div className='bg-white w-[70%] min-h-screen absolute right-0 p-[12px]'>
                <ScrollArea>
                    <div className='px-2'>
                        <div className='flex items-center justify-between text-2xl'>
                            <h1 className='font-sans font-medium text-gray-900 uppercase'>LEDGER VIEW</h1>
                            <div className='w-7 h-7 text-gray-700 flex items-center justify-center border border-black rounded-full hover:bg-black hover:text-white cursor-pointer' onClick={() => { props.onClose() }}>
                                <span><i className="las la-times text-xl cursor-pointer"></i></span>
                            </div>
                        </div>
                        <hr className='my-2' />
                        <div className='w-[40%]'>
                            {!isAccount() && <CardAccount loading={false} bg="bg-gray-100 border-prime" account={account} isMat={true} />}
                        </div>
                        <div className="w-full shadow-md rounded-md border mt-4">
                            <table className="min-w-full divide-y divide-gray-200 px-4">
                                <thead className="bg-gray-50">
                                    <tr className=''>
                                        <th scope="col" className="border-b border-gray-200" colSpan={6}>
                                            <div className='w-full flex items-center space-x-2 px-2'>
                                                <form className="py-2 flex-grow flex items-center">
                                                    <div className='flex'>
                                                        <Popover>
                                                            <PopoverTrigger asChild>
                                                                <Button id="date" variant={"outline"}
                                                                    className={cn(
                                                                        "w-[300px] justify-start text-left font-normal",
                                                                        !date && "text-muted-foreground"
                                                                    )}
                                                                >
                                                                    <CalendarIcon className="mr-2 h-4 w-4" />
                                                                    {date?.from ? (
                                                                        date.to ? (
                                                                            <>
                                                                                {Moment(date.from).format('MMM DD, yyyy')} -{" "}
                                                                                {Moment(date.to).format('MMM DD, yyyy')}
                                                                            </>
                                                                        ) : (
                                                                            Moment(date.from).format('MMM DD, yyyy')
                                                                        )
                                                                    ) : (
                                                                        <span>Pick a date</span>
                                                                    )}
                                                                </Button>
                                                            </PopoverTrigger>
                                                            <PopoverContent className="w-auto p-0" align="start">
                                                                <Calendar
                                                                    initialFocus
                                                                    mode="range"
                                                                    defaultMonth={date?.from}
                                                                    selected={date}
                                                                    onSelect={onDateChange}
                                                                    numberOfMonths={2}
                                                                />
                                                            </PopoverContent>
                                                        </Popover>
                                                    </div>
                                                    <div className='w-full flex items-center'>
                                                        <div className='w-[80px]'>
                                                            <p>Search :</p>
                                                        </div>
                                                        <input type="text" value={query} onChange={handleOnChange} name='search' placeholder="Search by narration." className="w-full h-10 px-4 font-sans text-sm outline-none rounded border" />
                                                    </div>
                                                </form>
                                                <div className='flex items-center'>
                                                    <DropdownMenu>
                                                        <DropdownMenuTrigger>
                                                            <button className="w-full py-1 px-2 h-10 rounded-md bg-prime hover:bg-primeLight text-white mr-4"><i className="las la-file-download mr-2"></i> Export </button>
                                                        </DropdownMenuTrigger>
                                                        <DropdownMenuContent>
                                                            <div
                                                                className='bg-white shadow-md rounded-md absolute' style={{ top: '0px', left: '-64px' }}>
                                                                <div>
                                                                    <ul className='flex flex-col items-start p-2'>
                                                                        <DropdownMenuItem>
                                                                            <li className="w-40 cursor-pointer p-2 rounded-md" onClick={() => {
                                                                                exportData('xlsx');
                                                                            }}>
                                                                                <div className='flex items-center text-base'>
                                                                                    <p className="text-sm font-serif ml-2 font-normal">Export to .xlsx</p>
                                                                                </div>
                                                                            </li>
                                                                        </DropdownMenuItem>
                                                                        <DropdownMenuItem>
                                                                            <li className="w-40 cursor-pointer p-2 rounded-md" onClick={() => {
                                                                                exportData('csv');
                                                                            }}>
                                                                                <div className='flex items-center text-base'>
                                                                                    <p className="text-sm font-serif ml-2 font-normal">Export to .csv</p>
                                                                                </div>
                                                                            </li>
                                                                        </DropdownMenuItem>
                                                                    </ul>
                                                                </div>
                                                            </div>
                                                        </DropdownMenuContent>
                                                    </DropdownMenu>
                                                </div>
                                            </div>
                                        </th>
                                    </tr>
                                    <tr>
                                        <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer">
                                            narration
                                        </th>
                                        <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer">
                                            Credit
                                        </th>
                                        <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer">
                                            Debit
                                        </th>
                                        <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer">
                                            Balance After
                                        </th>
                                        <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer">
                                            Status
                                        </th>
                                        <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer">
                                            Created At
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {
                                        records.length !== 0 && records.map((record) => {
                                            return <tr className="hover:bg-gray-100">
                                                <td className="px-4 py-4 whitespace-nowrap">
                                                    <div className="text-sm">
                                                        <span className='font-medium'>{record.narration}</span><br />
                                                        <span className='font-medium text-xs text-prime cursor-pointer'>#{record.accNumber}</span><br />
                                                    </div>
                                                </td>
                                                <td className="px-4 py-4 whitespace-nowrap">
                                                    <div className="text-sm">
                                                        <span className='font-semibold'>{record.transactionType === "credit" ? `${record.amount.toFixed(2)} Unit` : "-"}</span><br />
                                                    </div>
                                                </td>
                                                <td className="px-4 py-4 whitespace-nowrap">
                                                    <div className="text-sm">
                                                        <span className='font-semibold'>{record.transactionType === "credit" ? "-" : `${record.amount.toFixed(2)} Unit`}</span><br />
                                                    </div>
                                                </td>
                                                <td className="px-4 py-4 whitespace-nowrap">
                                                    <div className="text-sm">
                                                        <span className='font-semibold'>{record.balanceAfter.toFixed(2)} Unit</span><br />
                                                    </div>
                                                </td>
                                                <td className="px-4 py-4 whitespace-nowrap">
                                                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full uppercase ${getChipColor(record.status)}`}>
                                                        {record.status}
                                                    </span>
                                                </td>
                                                <td className="px-4 py-4 whitespace-nowrap">
                                                    <div className="text-sm text-blue-600">
                                                        {Moment(record.createdAt).format('DD MMM, yyyy')}
                                                    </div>
                                                </td>
                                            </tr>
                                        })
                                    }
                                    {
                                        records.length === 0 && <tr className="cursor-pointer hover:bg-gray-100">
                                            <td className="px-6 py-4 whitespace-nowrap" colSpan="6">
                                                <div className="text-sm text-red-600 text-center">
                                                    No data found.
                                                </div>
                                            </td>
                                        </tr>
                                    }
                                </tbody>
                            </table>
                            <div className='flex py-2 px-4 items-center bg-blue-50'>
                                <p className='text-sm font-serif'>Show rows per page</p>
                                <select className='text-sm ml-2 bg-gray-100' name="pageSize" onChange={handleOnChange} value={pageSize}>
                                    <option value={10}>10</option>
                                    <option value={20}>20</option>
                                    <option value={40}>40</option>
                                    <option value={50}>50</option>
                                </select>
                                <div className='flex-grow'></div>
                                <p className='text-sm font-serif mr-2'>Page {pageCurrent} of {pageTotal}</p>
                                <div onClick={() => { prevPage() }}><i className={`las la-angle-left w-6 h-6 flex items-center justify-center rounded-full hover:bg-gray-200 ${pageCurrent <= 1 ? 'text-gray-400 cursor-not-allowed' : 'text-black cursor-pointer'}`}></i></div>
                                <div onClick={() => { nextPage() }}><i className={`las la-angle-right w-6 h-6 flex items-center justify-center rounded-full hover:bg-gray-200 ml-2 text-black ${pageCurrent === pageTotal ? 'text-gray-400 cursor-not-allowed' : 'text-black cursor-pointer'}`}></i></div>
                            </div>
                        </div>
                    </div>
                </ScrollArea >
            </div >
        </div >
    )
}